// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import {
  getEmpresaEventosPublicadosActivos,
  getEmpresaEventosPorPublicar,
  getEmpresaEventosFinalizados,
} from "@/features/empresa/empresaThunks";
import {
  payPlanWithKlap,
  getListPagosPlanPorEmpresaId,
} from "@/features/suscripcion/suscripcionThunks";

// React and Router
import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Contextos
import { useModalsContext } from "@/contexts/ModalsContext";
// Forms
import CancelarEventoPublicadoTerminos from "@/components/Forms/CancelarEventoPublicadoTerminos";

// Skeleton
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

// Utils
import { shareContent } from "@/utils/shareUtil";
import { startActivarEmpresaFase2 } from "@/tours/tourActivarEmpresa";
import { startCrearEventoFase2 } from "@/tours/tourCrearEvento";

// Components
import Modal from "@/components/Modals/Modal";
import DropdownMenu from "@/components/DropdownMenu";
import Tab from "@/components/Tab";
import CrearEvento from "@/components/Forms/CrearEvento";

// Assets
import imgPortada from "@/assets/perfil/Banner Artist.webp";
import LogoPerfilEmpresa from "@/assets/avatars/avatar_amanda.webp";
import Ambassador1 from "@/assets/recentAmbassador/mujer1.webp";
import Ambassador2 from "@/assets/recentAmbassador/mujer2.webp";
import Ambassador3 from "@/assets/recentAmbassador/mujer3.webp";
import Ambassador4 from "@/assets/recentAmbassador/mujer4.webp";
import EventImage1 from "@/assets/eventos/ViejasDeMierda.webp";
import EventImage2 from "@/assets/eventos/ClubAmandaDJCeleArrabal.webp";
import EventImage3 from "@/assets/eventos/ClubAmandaFullBailableYElectronic.webp";
import EventImage4 from "@/assets/eventos/CelebrarEnAlonso.webp";
import EventImage5 from "@/assets/eventos/Ferxxo.webp";
import EventImage6 from "@/assets/eventos/JorisVoorn.webp";

// Components específicos de la vista
import Portada from "@/components/Perfil/Portada";
import InfoPerfil from "@/components/Perfil/InfoPerfil";
import OptionsPerfil from "@/components/Perfil/OptionsPerfil";
import ViewHandlers from "@/components/PerfilViewEmpresaOwnerEmpresa/ViewHandlers";
import TicketCarousel from "@/components/TicketCarousel/TicketCarousel";
import TicketCardPerfilEmpresaViewOwnEmpresa from "@/components/TicketCarousel/TicketCardPerfilEmpresaViewOwnEmpresa";
import StatsNumericos from "@/components/PerfilViewEmpresaOwnerEmpresa/StatsNumericos";
import StatsGenerales from "@/components/PerfilViewEmpresaOwnerEmpresa/StatsGenerales";
import DataTableStaff from "@/components/PerfilViewEmpresaOwnerEmpresa/DataTableStaff";
import DistribucionTicketsBar from "@/components/PerfilViewEmpresaOwnerEmpresa/DistribucionTicketsBar";
import EditarInformacionEmpresa from "@/components/PerfilViewEmpresaOwnerEmpresa/EditarInformacionEmpresa";
import SuscripcionTab from "@/components/PerfilViewEmpresaOwnerEmpresa/SuscripcionTab";

// Data Distribución de Tickets
const DistribucionTicketData = [
  {
    label: "Ventas",
    value: 100,
    color: "#84cc16", // lime-500
  },
  {
    label: "Invitación free",
    value: 100,
    color: "#f97316", // orange-500
  },
  {
    label: "Invitación con Descuento",
    value: 100,
    color: "#3b82f6", // blue-500
  },
];

export default function PerfilViewEmpresaOwnerEmpresa() {
  // Obtener la ubicación actual
  const location = useLocation();
  // Obtener el UID de la empresa de la ruta
  const { id } = useParams();
  // console.log("ID de la empresa desde la URL:", id);
  // Estados locales para el manejo de la carga de datos
  const [isLoading, setIsLoading] = useState(true);
  const [dataLoadComplete, setDataLoadComplete] = useState(false);

  // Redux state and dispatch
  const dispatch = useDispatch();
  const { infoEmpresa } = useSelector((state) => state.empresa);

  const { user } = useSelector((state) => state.auth);
  // console.log("Usuario autenticado en PerfilViewEmpresaOwnerEmpresa:", user);

  // Tab info for horizontal layout
  const HORIZONTAL_TAB_INFO = {
    title: "",
    description: "",
    layout: "horizontal",
    textColor: "text-white", // textColor de las tabs
    textColorHover: "hover:text-aw-green", // textColor al hacer hover en las tabs
    textColorActive: "text-aw-blueblack", // textColor de la tab activa
    bgColor: "bg-aw-bluedark-30", // bgColor del fondo de las tabs
    bgColorHover: "hover:bg-aw-bluedark-30", // bgColor al hacer hover en las tabs
    bgColorActive: "bg-aw-green", // bgColor de la tab activa
    borderColor: "border-aw-green",
    borderColorActive: "border-white",
    tabs: [
      {
        name: "Eventos",
        href: "#",
        current: true,
        icon: "fa-solid fa-calendar-days",
        contentClass: "py-6",
      },
    ],
  };

  HORIZONTAL_TAB_INFO.tabs.push({
    name: "Información",
    href: "#",
    current: false,
    icon: "fa-solid fa-circle-info",
    contentClass: "py-6 sm:p-6",
  });

  if (infoEmpresa?.published === 1) {
    HORIZONTAL_TAB_INFO.tabs.push({
      name: "Suscripción",
      href: "#",
      current: false,
      icon: "fa-solid fa-credit-card",
      contentClass: "py-6 sm:p-6",
    });
  }

  // console.log("infoEmpresa en PerfilViewEmpresaOwnerEmpresa:", infoEmpresa);

  useEffect(() => {
    // EmpresaProfileRouter ya cargó infoEmpresa. Solo cargar los eventos.
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Cargar las 3 secciones de eventos en paralelo
        const empresaUid = infoEmpresa?.uid || id;
        const [activos, noPublicados, finalizados] = await Promise.all([
          dispatch(
            getEmpresaEventosPublicadosActivos({ uid: empresaUid, page: 1, limit: 10 }),
          ).unwrap(),
          dispatch(
            getEmpresaEventosPorPublicar({ uid: empresaUid, page: 1, limit: 10 }),
          ).unwrap(),
          dispatch(
            getEmpresaEventosFinalizados({ uid: empresaUid, page: 1, limit: 10 }),
          ).unwrap(),
        ]);

        setEventosActivos(activos.eventos || []);
        setEventosPorPublicar(noPublicados.eventos || []);
        setEventosFinalizados(finalizados.eventos || []);

        setLoadingActivos(false);
        setLoadingNoPublicados(false);
        setLoadingFinalizados(false);

        // Marcar que los datos se han cargado completamente
        setDataLoadComplete(true);
      } catch (error) {
        console.error("Error al cargar los eventos de la empresa:", error);
        setLoadingActivos(false);
        setLoadingNoPublicados(false);
        setLoadingFinalizados(false);
        setDataLoadComplete(false);
      }
    };

    loadData();
  }, [dispatch, location.pathname]);

  // Verificar si la carga de eventos ha finalizado
  useEffect(() => {
    if (dataLoadComplete) {
      // Pequeño delay para asegurar que la pantalla de carga se vea
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [dataLoadComplete]);

  // Contextos
  const { modals, closeModal, openModal } = useModalsContext();

  // Estados
  const [visibleSection, setVisibleSection] = useState([
    { section: "quickStats", visible: true },
    { section: "handlers", visible: true },
    { section: "eventosActivos", visible: true },
    { section: "eventosPorPublicar", visible: true },
    { section: "eventosFinalizados", visible: true },
    { section: "dataGeneral", visible: true },
  ]);

  // Estados para las 3 secciones de eventos
  const [eventosActivos, setEventosActivos] = useState([]);
  const [eventosPorPublicar, setEventosPorPublicar] = useState([]);
  const [eventosFinalizados, setEventosFinalizados] = useState([]);

  // Estados de carga para cada sección
  const [loadingActivos, setLoadingActivos] = useState(true);
  const [loadingNoPublicados, setLoadingNoPublicados] = useState(true);
  const [loadingFinalizados, setLoadingFinalizados] = useState(true);

  // Manejador de visibilidad de secciones
  const handlerVisibleSection = (section) => {
    setVisibleSection((prev) =>
      prev.map((p) =>
        p.section === section ? { ...p, visible: !p.visible } : p,
      ),
    );
  };

  // Sample menu options
  let menuOptions = [
    {
      type: "none",
      classContent: "bg-aw-bluedark p-1.5",
    },
  ];

  switch (infoEmpresa?.published) {
    case 0:
      menuOptions.push(
        {
          type: "link",
          label: "Activar Empresa",
          icon: "fa-solid fa-certificate",
          to: `/empresa/${infoEmpresa?.uid}/activar`,
          className: "text-white hover:bg-aw-green/20",
          tourId: "menu-activar-empresa",
        },
        { divider: true },
      );
      break;
    case 2:
      menuOptions.push(
        {
          type: "link",
          label: "En Revisión",
          icon: "fa-solid fa-spinner",
          to: "#",
          className:
            "text-white bg-yellow-500/75 font-semibold cursor-not-allowed animate-pulse",
          tourId: "menu-empresa-en-revision",
        },
        { divider: true },
      );
      break;
  }

  // Función para compartir el perfil de la empresa
  const handleSharePerfil = async () => {
    const perfilUrl = `${window.location.origin}/empresa/${infoEmpresa?.alias || infoEmpresa?.uid}`;

    await shareContent({
      url: perfilUrl,
      title: infoEmpresa?.nombre || "Perfil de Empresa",
      text: `¡Conoce ${infoEmpresa?.nombre}!`,
      successMessage: "¡Link del perfil copiado al portapapeles!",
    });
  };

  menuOptions.push(
    {
      type: "button",
      label: "Crear Evento",
      icon: "fa-solid fa-ticket",
      onClick: () => openModal("crearEvento"),
      className: "text-white hover:bg-aw-green/20",
    },
    { divider: true },
  );

  menuOptions.push(
    {
      type: "button",
      label: "Compartir Perfil",
      icon: "fa-solid fa-share",
      onClick: handleSharePerfil,
      className: "text-white hover:bg-aw-green/20",
    },
    // {
    //   type: "button",
    //   label: "Copiar Enlace",
    //   icon: "fa-solid fa-link",
    //   onClick: () => {
    //     navigator.clipboard.writeText(window.location.href);
    //     alert("Enlace copiado");
    //   },
    //   className: "text-white hover:bg-aw-green/20",
    // }
    // { divider: true },
    // {
    //   type: "button",
    //   label: "Reportar",
    //   icon: "fa-solid fa-flag",
    //   onClick: () => console.log("Reportar"),
    //   className: "text-red-500 hover:bg-aw-green/20"
    // }
  );

  // Menú opciones sección Mis Tickets Activos
  const ticketsMenuOptions = [
    { type: "none", classContent: "bg-aw-bluedark p-1.5" },
    {
      type: "button",
      label: "Crear Evento",
      icon: "fa-solid fa-plus",
      onClick: () => openModal("crearEvento"),
      className: "text-white hover:bg-aw-green/20",
    },
    { divider: true },
    {
      type: "button",
      label: "Ver Tickets pasados",
      icon: "fa-solid fa-ticket",
      onClick: () => (window.location.href = "/perfil/mis-tickets"),
      className: "text-white hover:bg-aw-green/20",
    },
  ];

  // Tabs horizontal
  const horizontalContent = [<StatsGenerales />, <DataTableStaff />];

  const isLoadingVerify = isLoading || !dataLoadComplete;

  // Tour: Activar Empresa — Fase 2 (se activa cuando la página de empresa termina de cargar)
  useEffect(() => {
    if (isLoadingVerify) return;
    const pendingTour = sessionStorage.getItem("aw_pending_tour");
    if (pendingTour === "activar-empresa-fase2") {
      sessionStorage.removeItem("aw_pending_tour");
      setTimeout(() => startActivarEmpresaFase2(), 500);
    } else if (pendingTour === "crear-evento-fase2") {
      sessionStorage.removeItem("aw_pending_tour");
      setTimeout(() => startCrearEventoFase2(), 500);
    }
  }, [isLoadingVerify]);

  return (
    <>
      <div className="w-full max-w-5xl mx-auto relative">
        {/* Portada */}
        {isLoadingVerify ? (
          <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
            <Skeleton height={320} className="w-full rounded-b-3xl" />
          </SkeletonTheme>
        ) : (
          <Portada info={infoEmpresa} />
        )}
      </div>
      {/* Content Perfil */}
      <div className="w-full min-h-fit rounded-t-[8%] bg-gradient-to-b from-aw-bluedark from-20% via-aw-bluedark-85 via-70% to-bluedark-30 to-100% flex flex-col items-center pt-20 pb-4 -mt-20 sm:-mt-[5.5rem]">
        <div className="w-full max-w-5xl mx-auto relative">
          <div className="flex flex-col items-end sm:flex-row sm:justify-between sm:items-center">
            {/* Información del Perfil */}
            {isLoadingVerify ? (
              <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                <div className="w-full sm:w-1/2 flex flex-row items-end sm:items-center gap-4 px-2 z-10 relative">
                  <div className="relative">
                    <Skeleton
                      circle={true}
                      height={112}
                      width={112}
                      className="rounded-full border-4 border-white object-cover shadow-lg bg-aw-bluegray"
                    />
                  </div>
                  <div className="flex flex-col gap-2 mb-6">
                    <Skeleton height={24} width={200} borderRadius={8} />
                    <Skeleton height={20} width={150} borderRadius={8} />
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <InfoPerfil info={infoEmpresa} />
            )}
            {/* Opciones en empresa */}
            {isLoadingVerify ? (
              <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                <div className="flex w-full sm:w-1/2 mt-8 justify-center sm:items-center">
                  <div className="flex flex-col items-center w-full sm:items-end gap-2">
                    <div className="flex flex-col items-center w-full sm:items-end gap-2">
                      <div className="flex items-center -space-x-4 mb-1">
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <Skeleton circle={true} height={40} width={40} />
                        <span className="ml-6 text-gray-200 text-sm font-semibold">
                          <Skeleton height={16} width={80} />
                        </span>
                      </div>
                    </div>
                    <div className="flex w-full sm:w-1/2 justify-center sm:items-center">
                      <div className="flex flex-row justify-between gap-x-5 sm:gap-x-2">
                        <Skeleton height={40} width={120} borderRadius={8} />
                        <Skeleton height={40} width={40} borderRadius={8} />
                        <Skeleton height={40} width={40} borderRadius={8} />
                      </div>
                    </div>
                  </div>
                </div>
              </SkeletonTheme>
            ) : (
              <div className="flex flex-col items-end gap-2">
                {infoEmpresa?.totalSeguidores > 0 && (
                  <span className="text-gray-400 text-sm">
                    {infoEmpresa.totalSeguidores}{" "}
                    {infoEmpresa.totalSeguidores === 1 ? "seguidor" : "seguidores"}
                  </span>
                )}
                <OptionsPerfil
                  info={infoEmpresa}
                  people={null}
                  menuOptions={menuOptions}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats numéricos */}
      {/* <div className="w-full flex flex-col justify-between mb-8 px-2 gap-x-4">
        <h3 className="text-xl text-left font-semibold mb-3 text-white">
          Estadísticas Rápidas
          <FontAwesomeIcon
            onClick={() => handlerVisibleSection("quickStats")}
            icon={`fa-solid ${
              visibleSection.find((s) => s.section === "quickStats")?.visible
                ? "fa-eye"
                : "fa-eye-slash"
            }`}
            className="ml-3 text-aw-green cursor-pointer"
          />
        </h3>
        <div
          className={`w-full ${
            visibleSection.find((s) => s.section === "quickStats")?.visible
              ? "block"
              : "hidden"
          }`}
        >
          <StatsNumericos />
        </div>
      </div> */}
      {/* Handlers */}
      {/* <div className="w-full flex flex-col justify-between mb-8 px-2 gap-x-4 z-40">
        <h3 className="text-xl text-left font-semibold mb-3 text-white">
          Manejadores
          <FontAwesomeIcon
            onClick={() => handlerVisibleSection("handlers")}
            icon={`fa-solid ${
              visibleSection.find((s) => s.section === "handlers")?.visible
                ? "fa-eye"
                : "fa-eye-slash"
            }`}
            className="ml-3 text-aw-green cursor-pointer"
          />
        </h3>
        <div
          className={`w-full ${
            visibleSection.find((s) => s.section === "handlers")?.visible
              ? "block"
              : "hidden"
          }`}
        >
          <ViewHandlers />
        </div>
      </div> */}
      {/* Tabs: Eventos e Información */}
      <div className="w-full bg-aw-bluedark-30 flex flex-col items-center pt-20 -mt-20 sm:-mt-[5.5rem]">
        <Tab tabInfo={HORIZONTAL_TAB_INFO}>
          {/* Tab 1: Eventos (Por Publicar, Activos, Finalizados) */}
          <div>
            {/* Sección 2: Eventos No Publicados */}
            <div className="w-full mx-auto mb-8 px-2">
              <div className="w-full flex flex-row items-center justify-between mb-4 px-2 gap-x-4">
                <h3 className="text-xl text-left font-semibold text-white">
                  Eventos Por Publicar
                  <FontAwesomeIcon
                    onClick={() =>
                      handlerVisibleSection("eventosPorPublicar")
                    }
                    icon={`fa-solid ${
                      visibleSection.find(
                        (s) => s.section === "eventosPorPublicar",
                      )?.visible
                        ? "fa-eye"
                        : "fa-eye-slash"
                    }`}
                    className="ml-3 text-aw-green cursor-pointer"
                  />
                </h3>
              </div>
              {loadingNoPublicados ? (
                <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                  <div className="w-full flex flex-row rounded-xl gap-x-4">
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                  </div>
                </SkeletonTheme>
              ) : (
                <div
                  className={`relative ${
                    visibleSection.find(
                      (section) => section.section === "eventosPorPublicar",
                    )?.visible
                      ? "block"
                      : "hidden"
                  }`}
                >
                  <TicketCarousel
                    tickets={eventosPorPublicar}
                    TicketItem={TicketCardPerfilEmpresaViewOwnEmpresa}
                    idEmpresa={infoEmpresa?.uid}
                    additionalProps={{
                      empresaPublished: infoEmpresa?.published,
                      myself: true,
                    }}
                    sectionType="eventosPorPublicar"
                    uid={infoEmpresa?.uid || id}
                    onLoadMore={(newEventos) =>
                      setEventosPorPublicar((prev) => [
                        ...prev,
                        ...newEventos,
                      ])
                    }
                    onCreateClick={() => openModal("crearEvento")}
                  />
                </div>
              )}
            </div>

            {/* Sección 1: Eventos Publicados Activos */}
            <div className="w-full mx-auto mb-8 px-2">
              <div className="w-full flex flex-row items-center justify-between mb-4 px-2 gap-x-4">
                <h3 className="text-xl text-left font-semibold text-white">
                  Eventos Publicados Activos
                  <FontAwesomeIcon
                    onClick={() => handlerVisibleSection("eventosActivos")}
                    icon={`fa-solid ${
                      visibleSection.find((s) => s.section === "eventosActivos")
                        ?.visible
                        ? "fa-eye"
                        : "fa-eye-slash"
                    }`}
                    className="ml-3 text-aw-green cursor-pointer"
                  />
                </h3>
                {/* <div className="relative">
              <DropdownMenu
                trigger={
                  <button className="bg-transparent border border-white text-white font-semibold rounded-lg px-6 py-2 hover:bg-aw-green hover:text-aw-bluedark transition">
                    <span>Opciones</span>
                    <FontAwesomeIcon
                      icon={"fa-solid fa-ellipsis"}
                      className="ml-3"
                    />
                  </button>
                }
                options={ticketsMenuOptions}
                direction="down"
                align="end"
                classContent={ticketsMenuOptions[0]?.classContent || "white"}
              />
            </div> */}
              </div>
              {loadingActivos ? (
                <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                  <div className="w-full flex flex-row rounded-xl gap-x-4">
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                  </div>
                </SkeletonTheme>
              ) : (
                <div
                  className={`relative ${
                    visibleSection.find(
                      (section) => section.section === "eventosActivos",
                    )?.visible
                      ? "block"
                      : "hidden"
                  }`}
                >
                  {eventosActivos.length > 0 ? (
                    <TicketCarousel
                      tickets={eventosActivos}
                      TicketItem={TicketCardPerfilEmpresaViewOwnEmpresa}
                      idEmpresa={infoEmpresa?.uid}
                      additionalProps={{
                        empresaPublished: infoEmpresa?.published,
                        myself: true,
                      }}
                      sectionType="eventosActivos"
                      uid={id}
                      onLoadMore={(newEventos) =>
                        setEventosActivos((prev) => [...prev, ...newEventos])
                      }
                    />
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-gray-400">
                        No hay eventos publicados activos
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sección 3: Eventos Finalizados */}
            <div className="w-full mx-auto mb-8 px-2">
              <div className="w-full flex flex-row items-center justify-between mb-4 px-2 gap-x-4">
                <h3 className="text-xl text-left font-semibold text-white">
                  Eventos Finalizados
                  <FontAwesomeIcon
                    onClick={() => handlerVisibleSection("eventosFinalizados")}
                    icon={`fa-solid ${
                      visibleSection.find(
                        (s) => s.section === "eventosFinalizados",
                      )?.visible
                        ? "fa-eye"
                        : "fa-eye-slash"
                    }`}
                    className="ml-3 text-aw-green cursor-pointer"
                  />
                </h3>
              </div>
              {loadingFinalizados ? (
                <SkeletonTheme baseColor="#1e293b" highlightColor="#334155">
                  <div className="w-full flex flex-row rounded-xl gap-x-4">
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                    <div className="min-w-[200px] max-w-[200px]">
                      <Skeleton height={380} className="rounded-xl" />
                    </div>
                  </div>
                </SkeletonTheme>
              ) : (
                <div
                  className={`relative ${
                    visibleSection.find(
                      (section) => section.section === "eventosFinalizados",
                    )?.visible
                      ? "block"
                      : "hidden"
                  }`}
                >
                  {eventosFinalizados.length > 0 ? (
                    <TicketCarousel
                      tickets={eventosFinalizados}
                      TicketItem={TicketCardPerfilEmpresaViewOwnEmpresa}
                      idEmpresa={infoEmpresa?.uid}
                      additionalProps={{
                        empresaPublished: infoEmpresa?.published,
                        myself: true,
                        finally: true,
                      }}
                      sectionType="eventosFinalizados"
                      uid={id}
                      onLoadMore={(newEventos) =>
                        setEventosFinalizados((prev) => [
                          ...prev,
                          ...newEventos,
                        ])
                      }
                    />
                  ) : (
                    <div className="text-center py-10">
                      <p className="text-gray-400">
                        No hay eventos finalizados
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tab 2: Información de la Empresa */}
          <EditarInformacionEmpresa />

          {/* Tab 3: Suscripción */}
          <div className="w-full max-w-6xl mx-auto px-4">
            <SuscripcionTab
              empresaInfo={infoEmpresa}
              empresaId={infoEmpresa?.uid}
            />
          </div>
        </Tab>
      </div>
      {/* Distribución de Tickets */}
      {/* <div className="w-full mx-auto mb-10 bg-aw-bluedark-75 rounded-xl border border-gray-600 py-6 px-8">
        <DistribucionTicketsBar
          icon="fa-solid fa-ticket"
          title="Total Ticket"
          date="12/JUN/2025"
          total={DistribucionTicketData.reduce(
            (acc, item) => acc + item.value,
            0
          )}
          data={DistribucionTicketData}
        />
      </div> */}
      {/* Tab de Datos Generales */}
      {/* <div className="w-full flex flex-col justify-between mb-10 px-2 gap-x-4">
        <h3 className="text-xl text-left font-semibold mb-3 text-white">
          Datos Generales
          <FontAwesomeIcon
            onClick={() => handlerVisibleSection("dataGeneral")}
            icon={`fa-solid ${
              visibleSection.find((s) => s.section === "dataGeneral")?.visible
                ? "fa-eye"
                : "fa-eye-slash"
            }`}
            className="ml-3 text-aw-green cursor-pointer"
          />
        </h3>
        <div
          className={`w-full ${
            visibleSection.find((s) => s.section === "dataGeneral")?.visible
              ? "block"
              : "hidden"
          }`}
        >
          <Tab tabInfo={HORIZONTAL_TAB_INFO}>{horizontalContent}</Tab>
        </div>
      </div> */}

      {/* Modals Section */}
      {/* Modal Cancelar Evento */}
      {modals.cancelar && (
        <Modal
          isOpen={modals.cancelar}
          onClose={() => closeModal("cancelar")}
          large="max-w-4xl"
        >
          <CancelarEventoPublicadoTerminos />
        </Modal>
      )}
      {/* Modal para crear Evento */}
      {modals.crearEvento && (
        <Modal
          isOpen={modals.crearEvento}
          onClose={() => closeModal("crearEvento")}
          large="max-w-4xl"
        >
          <CrearEvento onClose={() => closeModal("crearEvento")} />
        </Modal>
      )}
    </>
  );
}
